// Utility function for debouncing
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Utility function for handling escape key
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');

  if (summaryElement) {
    // Use a small delay to ensure the dropdown is fully closed before focusing
    setTimeout(() => {
      summaryElement.focus();
    }, 10);
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.mobileFiltersChanged = false;
    this.listenersInitialized = false;
  }

  connectedCallback() {
    // Only initialize listeners once when first connected
    if (!this.listenersInitialized) {
      // Use a timeout to ensure DOM is fully ready
      setTimeout(() => {
        this.bindEventListeners();
        this.initializeFacetRemoveElements();
        this.listenersInitialized = true;
      }, 100);
    }
  }

  initializeFacetRemoveElements() {
    // Ensure all facet-remove elements are properly initialized
    const facetRemoveElements = this.querySelectorAll('facet-remove');
    facetRemoveElements.forEach(element => {
      if (!element._initialized) {
        element._initialized = true;
        // The custom element should handle its own initialization
      }
    });
  }

  bindEventListeners() {
    // Desktop form handling
    const desktopForm = this.querySelector('#FacetFiltersForm');
    if (desktopForm) {
      // Only set up desktop filter listeners for the main desktop form
      this.setupDesktopFilterListeners();
    }

    // Mobile form handling
    const mobileForm = this.querySelector('#FacetFiltersFormMobile');
    if (mobileForm) {
      mobileForm.addEventListener('input', this.onMobileFilterChange.bind(this));
      mobileForm.addEventListener('submit', this.onMobileFormSubmit.bind(this));
      // Only set up mobile drawer listeners for the mobile form
      this.setupMobileDrawerListeners();
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    // Add event delegation for facet-remove clicks
    this.addEventListener('click', this.onActiveFilterClick.bind(this));

    // Also listen for clicks on the document to catch any facet-remove clicks that might bubble up
    document.addEventListener('click', (event) => {
      const facetRemove = event.target.closest('facet-remove');
      if (facetRemove && this.contains(facetRemove)) {
        this.onActiveFilterClick(event);
      }
    });
  }

  bindDesktopSortListener() {
    // Add change event listener for sort dropdown
    const sortSelect = this.querySelector('.facet-filters__sort');
    if (sortSelect) {
      sortSelect.addEventListener('change', (event) => {
        // Apply sort change immediately on desktop
        const desktopForm = this.querySelector('#FacetFiltersForm');
        if (desktopForm) {
          const formParams = this.createSearchParams(desktopForm);
          this.onSubmitForm(formParams, event);
        }
      });
    }
  }

  setupDesktopFilterListeners() {
    this.bindDesktopSortListener();

    // Remove existing listeners to prevent duplicates
    const filterDropdowns = this.querySelectorAll('.facets__disclosure');

    // Only proceed if we actually have dropdowns
    if (filterDropdowns.length === 0) {
      return;
    }

    filterDropdowns.forEach((dropdown, index) => {
      // Create a unique handler for each dropdown to avoid conflicts
      const handler = (event) => {
        if (!event.target.open && event.target === dropdown) {
          // Check if this dropdown has an apply button
          const applyWrapper = dropdown.querySelector('.facets__apply-wrapper');
          const hasChecked = Array.from(dropdown.querySelectorAll('input[type="checkbox"]')).some(cb => cb.checked);
          
          // Only auto-apply if there's no apply button or no checked items
          // If apply button exists and items are checked, wait for button click
          if (!applyWrapper || !hasChecked) {
            // Dropdown closed, apply filters only if they changed
            setTimeout(() => {
              this.applyDesktopFiltersIfChanged();
            }, 150);
          }
        }
      };

      // Store the handler for cleanup
      dropdown._facetToggleHandler = handler;

      // Remove existing listener if any
      if (dropdown._facetToggleHandler) {
        dropdown.removeEventListener('toggle', dropdown._facetToggleHandler);
      }

      // Add new listener
      dropdown.addEventListener('toggle', handler);
    });

    // Setup apply button functionality for each filter dropdown
    this.setupApplyButtons();
  }

  setupApplyButtons() {
    const filterDropdowns = this.querySelectorAll('.facets__disclosure');
    
    filterDropdowns.forEach((dropdown) => {
      const applyWrapper = dropdown.querySelector('.facets__apply-wrapper');
      if (!applyWrapper) return;

      const applyButton = applyWrapper.querySelector('.facets__apply-button');
      const checkboxes = dropdown.querySelectorAll('input[type="checkbox"]');
      
      // Store initial state when dropdown opens
      const getInitialState = () => {
        return Array.from(checkboxes).map(cb => ({
          id: cb.id,
          checked: cb.checked
        }));
      };
      
      // Initialize with current state
      let initialState = getInitialState();
      
      // Reset initial state when dropdown opens
      const handleDropdownOpen = () => {
        if (dropdown.open) {
          initialState = getInitialState();
          updateApplyButton();
        }
      };
      
      // Function to update button visibility
      const updateApplyButton = () => {
        const currentState = Array.from(checkboxes).map(cb => ({
          id: cb.id,
          checked: cb.checked
        }));
        
        // Check if state has changed from initial
        const stateChanged = initialState.length !== currentState.length || 
          initialState.some((initial, index) => {
            const current = currentState[index];
            return !current || current.checked !== initial.checked;
          });
        
        // We still compute stateChanged/hasChecked if needed later,
        // but we no longer hide/show the wrapper here to keep it always visible.
        const hasChecked = Array.from(checkboxes).some(checkbox => checkbox.checked);
        // Intentionally do NOT set applyWrapper.style.display here to avoid any display: none.
      };

      // Check initial state
      updateApplyButton();

      // Listen for dropdown toggle to reset initial state
      dropdown.addEventListener('toggle', handleDropdownOpen);

      // Listen for checkbox changes - use event delegation on the dropdown to handle dynamic checkboxes
      const handleCheckboxChange = (event) => {
        if (event.target.matches('input[type="checkbox"]') && dropdown.contains(event.target)) {
          updateApplyButton();
        }
      };
      
      // Remove old listener if exists
      if (dropdown._applyButtonChangeHandler) {
        dropdown.removeEventListener('change', dropdown._applyButtonChangeHandler);
      }
      dropdown._applyButtonChangeHandler = handleCheckboxChange;
      dropdown.addEventListener('change', handleCheckboxChange);

      // Handle apply button click
      if (applyButton) {
        // Remove old listener if exists
        if (applyButton._clickHandler) {
          applyButton.removeEventListener('click', applyButton._clickHandler);
        }
        
        applyButton._clickHandler = (event) => {
          event.preventDefault();
          event.stopPropagation();
          
          // Close the dropdown
          dropdown.removeAttribute('open');
          
          // Apply filters
          setTimeout(() => {
            this.applyDesktopFiltersIfChanged();
          }, 150);
        };
        
        applyButton.addEventListener('click', applyButton._clickHandler);
      }
    });
  }

  setupMobileDrawerListeners() {
    // Listen for custom events from menu-drawer
    const mobileForm = this.querySelector('#FacetFiltersFormMobile');
    if (mobileForm) {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (menuDrawer) {
        // Listen for drawer closing event
        menuDrawer.addEventListener('drawer:closing', () => {
          this.onMobileDrawerClose();
        });
      }
    }
  }

  onMobileFilterChange(event) {
    // Store the change but don't apply yet - wait for drawer close
    this.mobileFiltersChanged = true;
  }

  applyDesktopFiltersIfChanged() {
    const desktopForm = document.querySelector('#FacetFiltersForm');
    if (!desktopForm) return;

    const formParams = this.createSearchParams(desktopForm);
    const currentParams = window.location.search.slice(1);

    // Compare the form state with current URL parameters
    if (this.hasFiltersChanged(formParams, currentParams)) {
      this.onSubmitForm(formParams, null);
    }
  }

  hasFiltersChanged(formParams, currentParams) {
    // Simple string comparison first
    if (formParams === currentParams) {
      return false;
    }

    // Normalize both by removing empty parameters
    const formURLParams = new URLSearchParams(formParams);
    const currentURLParams = new URLSearchParams(currentParams);

    // Filter out empty values
    const formFiltered = new URLSearchParams();
    const currentFiltered = new URLSearchParams();

    for (const [key, value] of formURLParams) {
      if (value && value.trim() !== '') {
        formFiltered.append(key, value);
      }
    }

    for (const [key, value] of currentURLParams) {
      if (value && value.trim() !== '') {
        currentFiltered.append(key, value);
      }
    }

    // Handle default sort value - if form has 'best-selling' sort but URL doesn't have sort param,
    // and the current sort is 'best-selling', don't consider this a change
    const formSort = formFiltered.get('sort_by');
    const currentSort = currentFiltered.get('sort_by');

    if (formSort === 'best-selling' && !currentSort) {
      // Remove sort_by from form params for comparison since it's the default
      formFiltered.delete('sort_by');
    }

    // Convert to sorted strings for comparison
    const formSorted = new URLSearchParams([...formFiltered.entries()].sort()).toString();
    const currentSorted = new URLSearchParams([...currentFiltered.entries()].sort()).toString();

    const hasChanged = formSorted !== currentSorted;
    return hasChanged;
  }

  applyMobileFilters() {
    const mobileForm = document.querySelector('#FacetFiltersFormMobile');
    if (mobileForm && this.mobileFiltersChanged) {
      const searchParams = this.createSearchParams(mobileForm);
      this.onSubmitForm(searchParams, null);
      this.mobileFiltersChanged = false;
    }
  }

  static setListeners() {
    const onHistoryChange = event => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams !== FacetFiltersForm.searchParamsPrev) {
        FacetFiltersForm.renderPage(searchParams, null, false);
      }
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach(element => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner, .loading-overlay .loading__spinner').forEach(spinner => {
      spinner.classList.remove('hidden');
    });

    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');

    sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];

        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html.innerHTML);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(html.innerHTML);
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document.getElementById('ProductGridContainer').querySelectorAll('.scroll-trigger').forEach(element => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    container.innerHTML = count;
    container.classList.remove('loading');

    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }

    document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach(spinner => {
      spinner.classList.add('hidden');
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');
    const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter');

    Array.from(facetDetailsElementsFromDom).forEach(currentElement => {
      const hasMatchingId = Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id);
      if (!hasMatchingId) {
        currentElement.remove();
      }
    });

    const matchesId = element => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(element => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      if (document.getElementById(elementToRender.id)) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? '.mobile-facets__close-button'
          : '.facets__summary';
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);
        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) {
          newElementToActivate.focus();
        }
      }
    }
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach(selector => {
      if (html.querySelector(selector)) {
        document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
      }
    });

    // Re-bind desktop sort listener after DOM update
    const facetFiltersForm = document.querySelector('facet-filters-form');
    if (facetFiltersForm && typeof facetFiltersForm.bindDesktopSortListener === 'function') {
      facetFiltersForm.bindDesktopSortListener();
    }

    // Re-setup apply buttons after DOM update
    if (facetFiltersForm && typeof facetFiltersForm.setupApplyButtons === 'function') {
      facetFiltersForm.setupApplyButtons();
    }

    // Only re-bind menu-drawer events (these are needed for mobile drawer functionality)
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
        menuDrawer.bindEvents();
      }
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      if (target.querySelector('show-more-button .label-show-more.hidden')) {
        sourceWrapElement.querySelectorAll('.facets__item.hidden').forEach(hiddenItem => {
          hiddenItem.classList.replace('hidden', 'show-more-item');
        });
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    // Clean up empty parameters
    const urlParams = new URLSearchParams(searchParams || '');
    const cleanParams = new URLSearchParams();

    for (const [key, value] of urlParams) {
      if (value && value.trim() !== '') {
        cleanParams.append(key, value);
      }
    }

    const finalParams = cleanParams.toString();
    const newUrl = finalParams ? `${window.location.pathname}?${finalParams}` : window.location.pathname;

    history.pushState({ searchParams: finalParams }, '', newUrl);
  }

  static getSections() {
    return [{
      section: document.getElementById('product-grid').dataset.id
    }];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }



  // Method to be called when mobile drawer closes
  onMobileDrawerClose() {
    this.applyMobileFilters();
  }

  // Method to be called when mobile form is submitted
  onMobileFormSubmit(event) {
    event.preventDefault();
    this.applyMobileFilters();

    // Close the mobile drawer
    const menuDrawer = this.querySelector('#FacetFiltersFormMobile')?.closest('menu-drawer');
    if (menuDrawer) {
      const detailsElement = menuDrawer.querySelector('details');
      if (detailsElement) {
        detailsElement.removeAttribute('open');
      }
    }
  }

  // Handle removing active filters (facet-remove clicks)
  onActiveFilterClick(event) {
    const facetRemove = event.target.closest('facet-remove');
    if (facetRemove) {
      event.preventDefault();

      const link = facetRemove.querySelector('a');
      if (link && link.href) {

        try {
          // Extract search parameters from the link href
          const url = new URL(link.href);
          const searchParams = url.search.slice(1);

          FacetFiltersForm.renderPage(searchParams, event);
        } catch (error) {
          // Fallback: try to extract params manually
          const href = link.href;
          const questionMarkIndex = href.indexOf('?');
          if (questionMarkIndex !== -1) {
            const searchParams = href.substring(questionMarkIndex + 1);
            FacetFiltersForm.renderPage(searchParams, event);
          }
        }
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach(element => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) {
      minInput.setAttribute('data-max', maxInput.value);
    }

    if (minInput.value) {
      maxInput.setAttribute('data-min', minInput.value);
    }

    if (minInput.value === '') {
      maxInput.setAttribute('data-min', 0);
    }

    if (maxInput.value === '') {
      maxInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
    }
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) {
      input.value = min;
    }

    if (value > max) {
      input.value = max;
    }
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (facetLink) {
      facetLink.setAttribute('role', 'button');
      // Remove the click event listener since we're using event delegation
      // facetLink.addEventListener('click', this.closeFilter.bind(this));
      facetLink.addEventListener('keyup', event => {
        event.preventDefault();
        if (event.code.toUpperCase() === 'SPACE') {
          this.closeFilter(event);
        }
      });
    }
  }

  closeFilter(event) {
    event.preventDefault();
    // Find the parent facet-filters-form and call onActiveFilterClick
    const parentForm = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    if (parentForm && typeof parentForm.onActiveFilterClick === 'function') {
      parentForm.onActiveFilterClick(event);
    }
  }
}

customElements.define('facet-remove', FacetRemove);

// Facets accessibility enhancement
class FacetsAccessibility {
  constructor() {
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.updateInitialStates();
    this.setupFocusManagement();
  }

  setupEventListeners() {
    // Listen for details toggle events
    document.addEventListener('toggle', this.handleDetailsToggle.bind(this));

    // Listen for custom events from facet components
    document.addEventListener('facet:updated', this.handleFacetUpdate.bind(this));
  }

  setupFocusManagement() {
    // Track all open dropdowns
    this.openDropdowns = new Set();

    // Track pointer interactions to distinguish from keyboard navigation
    this.pointerDownTarget = null;

    // Listen for focus changes
    document.addEventListener('focusin', this.handleFocusIn.bind(this));
    document.addEventListener('focusout', this.handleFocusOut.bind(this));

    // Listen for pointer events to track mouse/touch interactions
    document.addEventListener('pointerdown', this.handlePointerDown.bind(this));

    // Listen for escape key to close dropdowns
    document.addEventListener('keydown', this.handleKeyDown.bind(this));
  }

  handleFocusIn(event) {
    const target = event.target;
    const details = target.closest('details');

    if (details && details.classList.contains('facets__disclosure') ||
      details && details.classList.contains('mobile-facets__details')) {
      // Focus is inside a dropdown, mark it as open
      this.openDropdowns.add(details);
    }
  }

  handlePointerDown(event) {
    // Track the element that was clicked/touched
    this.pointerDownTarget = event.target;
  }

  handleFocusOut(event) {
    const target = event.target;
    const details = target.closest('details');

    if (details && this.openDropdowns.has(details)) {
      // If this focusout was caused by a pointer interaction, don't close
      // This prevents closing when clicking on non-focusable elements inside the dropdown
      if (this.pointerDownTarget && details.contains(this.pointerDownTarget)) {
        this.pointerDownTarget = null; // Reset for next interaction
        return;
      }

      // For keyboard navigation, use event.relatedTarget to check where focus is going
      const relatedTarget = event.relatedTarget;

      if (relatedTarget && details.contains(relatedTarget)) {
        // Focus is moving to another element within the same dropdown
        return;
      }

      // Only auto-close desktop dropdowns when focus leaves
      // Mobile dropdowns should stay open to allow multiple filters
      if (details.classList.contains('facets__disclosure')) {
        // Desktop dropdown - close when focus leaves
        this.closeDropdown(details, false);
      }
      // Mobile dropdowns (mobile-facets__details) stay open
    }
  }


  handleKeyDown(event) {
    if (event.key === 'Escape') {
      // Close all open dropdowns when Escape is pressed
      this.openDropdowns.forEach(details => {
        this.closeDropdown(details, true); // Pass true to indicate escape key was used
      });
    }
  }

  closeDropdown(details, fromEscape = false) {
    if (details.open) {
      // Store reference to summary before closing
      const summary = details.querySelector('summary');

      details.removeAttribute('open');
      this.openDropdowns.delete(details);

      // Update accessibility state
      if (summary) {
        this.updateSummaryAccessibility(summary, false);

        // If closed with escape key, focus the summary for better accessibility
        if (fromEscape && summary) {
          this.restoreFocusToSummary(summary);
        }
      }

      // Dispatch custom event for other components
      details.dispatchEvent(new CustomEvent('facet:closed', {
        detail: { filterId: details.id, fromEscape }
      }));
    }
  }

  restoreFocusToSummary(summary) {
    // Use a small delay to ensure the dropdown is fully closed
    setTimeout(() => {
      // Check if the summary is still in the DOM and focusable
      if (summary && document.contains(summary) && summary.tabIndex !== -1) {
        summary.focus();

        // Dispatch a custom event for other components that might need to know
        summary.dispatchEvent(new CustomEvent('facet:summary-focused', {
          detail: { filterId: summary.closest('details')?.id }
        }));
      }
    }, 10);
  }

  handleDetailsToggle(event) {
    const details = event.target;
    const summary = details.querySelector('summary');

    if (summary && summary.classList.contains('facets__summary')) {
      this.updateSummaryAccessibility(summary, details.open);

      // Track open dropdowns
      if (details.open) {
        this.openDropdowns.add(details);
      } else {
        this.openDropdowns.delete(details);
      }
    }
  }

  handleFacetUpdate(event) {
    const { filterId, isExpanded } = event.detail;
    const details = document.getElementById(filterId);

    if (details) {
      const summary = details.querySelector('summary');
      if (summary) {
        this.updateSummaryAccessibility(summary, isExpanded);

        // Track open dropdowns
        if (isExpanded) {
          this.openDropdowns.add(details);
        } else {
          this.openDropdowns.delete(details);
        }
      }
    }
  }

  updateSummaryAccessibility(summary, isExpanded) {
    // Update aria-expanded
    summary.setAttribute('aria-expanded', isExpanded.toString());

    // Update aria-label based on state
    const filterLabel = summary.querySelector('.facets__summary-label')?.textContent?.trim() ||
      summary.querySelector('span')?.textContent?.trim() ||
      'Filter';

    const activeCount = this.getActiveFilterCount(summary);
    let ariaLabel;

    if (activeCount > 0) {
      ariaLabel = `${filterLabel} - ${this.getLocalizedText('products.facets.filters_selected', activeCount)}`;
    } else {
      ariaLabel = `${filterLabel} - ${this.getLocalizedText('products.facets.expand_filter')}`;
    }

    summary.setAttribute('aria-label', ariaLabel);
  }

  getActiveFilterCount(summary) {
    const details = summary.closest('details');
    if (!details) return 0;

    const activeInputs = details.querySelectorAll('input[type="checkbox"]:checked');
    return activeInputs.length;
  }

  getLocalizedText(key, count = null) {
    // This is a simplified version - in a real implementation,
    // you'd want to use Shopify's localization system
    const translations = {
      'products.facets.filters_selected': count === 1 ? '1 selected' : `${count} selected`,
      'products.facets.expand_filter': 'Expand filter'
    };

    return translations[key] || key;
  }

  updateInitialStates() {
    // Update all summary elements with their initial states
    const summaries = document.querySelectorAll('.facets__summary, .mobile-facets__summary');

    summaries.forEach(summary => {
      const details = summary.closest('details');
      if (details) {
        this.updateSummaryAccessibility(summary, details.open);

        // Track initially open dropdowns
        if (details.open) {
          this.openDropdowns.add(details);
        }
      }
    });
  }

  // Public method to programmatically close all dropdowns
  closeAllDropdowns() {
    this.openDropdowns.forEach(details => {
      this.closeDropdown(details, false); // Not from escape key
    });
  }

  // Public method to check if any dropdowns are open
  hasOpenDropdowns() {
    return this.openDropdowns.size > 0;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new FacetsAccessibility());
} else {
  new FacetsAccessibility();
}

// Export for use in other modules
window.FacetsAccessibility = FacetsAccessibility;